/**
 * PDF Annotation Service - CRUD for comment pins on PDFs
 */

import { db } from './firebase';
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  Timestamp,
  runTransaction,
} from 'firebase/firestore';
import type { PdfAnnotation, PdfAnnotationReply, AnnotationSource, BiddingAgentAnnotationMeta } from '../types';
import { applyReactionToggle, normalizeCommentReactions } from './commentReactions';
import {
  commentAuthorIdFromData,
  recordTaskCommentReactionEvent,
  shouldRecordCommentReactionEvent,
} from './commentReactionEvents';
import { markPdfCommentNotificationsAsRead } from './notificationReadSync';

export {
  collectProjectIdsWithPdfMentionsForUser,
  pdfAnnotationHasUnreadForUser,
  pdfAnnotationHasUnreadMention,
  pdfAnnotationMentionsUser,
  pdfAnnotationMentionsUserDeep,
} from './pdfAnnotationMentionUtils';

const COLLECTION = 'pdfAnnotations';

export type PdfAnnotationQueryScope = {
  projectId?: string;
  /** Architects must query decision pins only — rules hide working notes. */
  decisionOnly?: boolean;
};

function annotationsForDocumentQuery(
  field: 'documentId' | 'documentUrl',
  value: string,
  scope?: string | PdfAnnotationQueryScope,
) {
  const projectId = typeof scope === 'string' ? scope : scope?.projectId;
  const decisionOnly = typeof scope === 'object' && scope?.decisionOnly;
  const constraints = [where(field, '==', value)];
  if (projectId) constraints.push(where('projectId', '==', projectId));
  if (decisionOnly) constraints.push(where('audience', '==', 'architect'));
  return query(collection(db, COLLECTION), ...constraints);
}

function fromFirestore(docSnap: { id: string; data: () => Record<string, unknown> }): PdfAnnotation {
  const d = docSnap.data();
  const replies = d.replies as Array<{ userId: string; userName: string; content: string; mentions?: Array<{ userId: string; userName: string; startIndex: number; endIndex: number }>; createdAt: { toDate: () => Date } }> | undefined;
  const mentions = d.mentions as Array<{ userId: string; userName: string; startIndex: number; endIndex: number }> | undefined;
  const readBy = d.readBy as string[] | undefined;
  return {
    id: docSnap.id,
    documentId: d.documentId as string,
    documentUrl: d.documentUrl as string,
    leadId: d.leadId as string,
    pageNumber: d.pageNumber as number,
    x: d.x as number,
    y: d.y as number,
    content: d.content as string,
    userId: d.userId as string,
    userName: d.userName as string,
    mentions: mentions ?? undefined,
    reactions: normalizeCommentReactions(d.reactions),
    replies: replies?.map(r => ({
      userId: r.userId,
      userName: r.userName,
      content: r.content,
      mentions: r.mentions ?? undefined,
      reactions: normalizeCommentReactions((r as { reactions?: unknown }).reactions),
      createdAt: r.createdAt?.toDate?.() ?? new Date(),
    })),
    readBy: readBy ?? undefined,
    taskId: (d.taskId as string) || undefined,
    projectId: (d.projectId as string) || undefined,
    source: (d.source as AnnotationSource) || undefined,
    agentMeta: (d.agentMeta as BiddingAgentAnnotationMeta) || undefined,
    contextPreviewUrl: typeof d.contextPreviewUrl === 'string' ? d.contextPreviewUrl : undefined,
    planReviewId: typeof d.planReviewId === 'string' ? d.planReviewId : undefined,
    audience: d.audience === 'architect' ? 'architect' : d.audience === 'internal' ? 'internal' : undefined,
    createdAt: (d.createdAt as { toDate: () => Date })?.toDate?.() ?? new Date(d.createdAt as string),
  };
}

/** Create an annotation */
export async function createAnnotation(annotation: Omit<PdfAnnotation, 'id'>): Promise<string> {
  const docData: Record<string, unknown> = {
    documentId: annotation.documentId,
    documentUrl: annotation.documentUrl,
    leadId: annotation.leadId,
    pageNumber: annotation.pageNumber,
    x: annotation.x,
    y: annotation.y,
    content: annotation.content,
    userId: annotation.userId,
    userName: annotation.userName,
    mentions: annotation.mentions ?? [],
    replies: annotation.replies ?? [],
    createdAt: Timestamp.fromDate(annotation.createdAt),
  };
  if (annotation.taskId) docData.taskId = annotation.taskId;
  if (annotation.projectId) docData.projectId = annotation.projectId;
  if (annotation.contextPreviewUrl) docData.contextPreviewUrl = annotation.contextPreviewUrl;
  if (annotation.planReviewId) docData.planReviewId = annotation.planReviewId;
  docData.audience = annotation.audience === 'architect' ? 'architect' : 'internal';
  docData.readBy = [annotation.userId];
  const ref = await addDoc(collection(db, COLLECTION), docData);
  return ref.id;
}

/** Delete an annotation */
export async function deleteAnnotation(annotationId: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, annotationId));
}

/** Mark a PDF annotation thread as read by a user (files list, pins, lead feed). */
export async function markAnnotationAsRead(annotationId: string, userId: string): Promise<void> {
  const annRef = doc(db, COLLECTION, annotationId);
  const docSnap = await getDoc(annRef);
  if (!docSnap.exists()) {
    await markPdfCommentNotificationsAsRead(userId, annotationId);
    return;
  }
  const data = docSnap.data();
  const readBy = (data.readBy as string[]) || [];
  if (!readBy.includes(userId)) {
    await updateDoc(annRef, {
      readBy: [...readBy, userId],
    });
  }
  await markPdfCommentNotificationsAsRead(userId, annotationId);
}

/** Mark a PDF annotation thread as unread for a user (removes them from `readBy`). */
export async function markAnnotationAsUnread(annotationId: string, userId: string): Promise<void> {
  const annRef = doc(db, COLLECTION, annotationId);
  const docSnap = await getDoc(annRef);
  if (!docSnap.exists()) return;
  const data = docSnap.data();
  const readBy = (data.readBy as string[]) || [];
  if (!readBy.includes(userId)) return;
  await updateDoc(annRef, {
    readBy: readBy.filter((id) => id !== userId),
  });
}

/** Update annotation position (for drag) */
export async function updateAnnotationPosition(
  annotationId: string,
  x: number,
  y: number
): Promise<void> {
  await updateDoc(doc(db, COLLECTION, annotationId), { x, y });
}

/** Get annotations for a document (by documentId or documentUrl for comment attachments) */
export async function getAnnotationsForDocument(documentId: string, documentUrl: string): Promise<PdfAnnotation[]> {
  const q = query(
    collection(db, COLLECTION),
    where('documentId', '==', documentId)
  );
  const snapshot = await getDocs(q);
  let annotations = snapshot.docs.map(d => fromFirestore({ id: d.id, data: () => d.data() }));

  // Also fetch by documentUrl for comment attachments that use URL as documentId
  if (annotations.length === 0 && documentUrl) {
    const q2 = query(collection(db, COLLECTION), where('documentUrl', '==', documentUrl));
    const snap2 = await getDocs(q2);
    annotations = snap2.docs.map(d => fromFirestore({ id: d.id, data: () => d.data() }));
  }

  return annotations.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
}

/** Get all annotations for a lead (for unified comment feed) */
export async function getAnnotationsForLead(leadId: string): Promise<PdfAnnotation[]> {
  const q = query(collection(db, COLLECTION), where('leadId', '==', leadId));
  const snapshot = await getDocs(q);
  return snapshot.docs
    .map(d => fromFirestore({ id: d.id, data: () => d.data() }))
    .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
}

/** Subscribe to all annotations for a lead (real-time) */
export function subscribeToAnnotationsForLead(
  leadId: string,
  callback: (annotations: PdfAnnotation[]) => void
): () => void {
  const q = query(collection(db, COLLECTION), where('leadId', '==', leadId));
  return onSnapshot(q, snapshot => {
    const list = snapshot.docs
      .map(d => fromFirestore({ id: d.id, data: () => d.data() }))
      .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
    callback(list);
  });
}

/** Subscribe to annotations tied to a project (Project Hub files — projectId on annotation doc). */
export function subscribeToAnnotationsForProject(
  projectId: string,
  callback: (annotations: PdfAnnotation[]) => void,
  options?: { decisionOnly?: boolean },
): () => void {
  const q = options?.decisionOnly
    ? query(
        collection(db, COLLECTION),
        where('projectId', '==', projectId),
        where('audience', '==', 'architect'),
      )
    : query(collection(db, COLLECTION), where('projectId', '==', projectId));
  return onSnapshot(q, snapshot => {
    const list = snapshot.docs
      .map(d => fromFirestore({ id: d.id, data: () => d.data() }))
      .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
    callback(list);
  });
}

const FIRESTORE_IN_QUERY_MAX = 10;

/**
 * Subscribe to pdfAnnotations for many projects using batched `projectId in (...)` queries
 * (Firestore limits `in` to 10 values per query). Emits a map of projectId → annotations.
 */
export function subscribeToAnnotationsForProjectIds(
  projectIds: string[],
  callback: (byProjectId: Map<string, PdfAnnotation[]>) => void,
  options?: { decisionOnly?: boolean },
): () => void {
  const unique = Array.from(new Set(projectIds.filter(Boolean)));
  if (unique.length === 0) {
    callback(new Map());
    return () => {};
  }

  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += FIRESTORE_IN_QUERY_MAX) {
    chunks.push(unique.slice(i, i + FIRESTORE_IN_QUERY_MAX));
  }

  const chunkAnnotIds = new Map<number, Set<string>>();
  const allById = new Map<string, PdfAnnotation>();

  const emit = () => {
    const byProject = new Map<string, PdfAnnotation[]>();
    Array.from(allById.values()).forEach((ann) => {
      const pid = ann.projectId;
      if (!pid) return;
      const arr = byProject.get(pid);
      if (arr) arr.push(ann);
      else byProject.set(pid, [ann]);
    });
    Array.from(byProject.values()).forEach((arr) => {
      arr.sort((a: PdfAnnotation, b: PdfAnnotation) => a.createdAt.getTime() - b.createdAt.getTime());
    });
    callback(byProject);
  };

  const unsubs = chunks.map((ids, chunkIndex) => {
    const q = options?.decisionOnly
      ? query(
          collection(db, COLLECTION),
          where('projectId', 'in', ids),
          where('audience', '==', 'architect'),
        )
      : query(collection(db, COLLECTION), where('projectId', 'in', ids));
    return onSnapshot(q, (snapshot) => {
      const prevIds = chunkAnnotIds.get(chunkIndex) ?? new Set<string>();
      prevIds.forEach((id) => {
        allById.delete(id);
      });
      const nextIds = new Set<string>();
      snapshot.docs.forEach((d) => {
        const a = fromFirestore({ id: d.id, data: () => d.data() });
        if (a.id) {
          allById.set(a.id, a);
          nextIds.add(a.id);
        }
      });
      chunkAnnotIds.set(chunkIndex, nextIds);
      emit();
    });
  });

  return () => {
    unsubs.forEach((u) => u());
  };
}

/** Subscribe to annotations for a document (real-time updates) */
export function subscribeToAnnotationsForDocument(
  documentId: string,
  documentUrl: string,
  callback: (annotations: PdfAnnotation[]) => void,
  onError?: (error: Error) => void,
  projectId?: string | PdfAnnotationQueryScope,
): () => void {
  const unsubs: (() => void)[] = [];
  const mergeAndSort = (byId: PdfAnnotation[], byUrl: PdfAnnotation[]) => {
    const seen = new Set<string>();
    const merged: PdfAnnotation[] = [];
    for (const a of [...byId, ...byUrl]) {
      if (a.id && !seen.has(a.id)) {
        seen.add(a.id);
        merged.push(a);
      } else if (!a.id) merged.push(a);
    }
    callback(merged.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime()));
  };
  let byId: PdfAnnotation[] = [];
  let byUrl: PdfAnnotation[] = [];
  const maybeEmit = () => mergeAndSort(byId, byUrl);
  const handleError = (error: Error) => {
    onError?.(error);
  };
  unsubs.push(
    onSnapshot(
      annotationsForDocumentQuery('documentId', documentId, projectId),
      snapshot => {
        byId = snapshot.docs.map(d => fromFirestore({ id: d.id, data: () => d.data() }));
        maybeEmit();
      },
      handleError,
    )
  );
  if (documentUrl && documentUrl !== documentId) {
    unsubs.push(
      onSnapshot(
        annotationsForDocumentQuery('documentUrl', documentUrl, projectId),
        snapshot => {
          byUrl = snapshot.docs.map(d => fromFirestore({ id: d.id, data: () => d.data() }));
          maybeEmit();
        },
        handleError,
      )
    );
  }
  return () => unsubs.forEach(u => u());
}

/** Subscribe to all annotations for a task (real-time) */
export function subscribeToAnnotationsForTask(
  taskId: string,
  callback: (annotations: PdfAnnotation[]) => void
): () => void {
  const q = query(collection(db, COLLECTION), where('taskId', '==', taskId));
  return onSnapshot(q, snapshot => {
    const list = snapshot.docs
      .map(d => fromFirestore({ id: d.id, data: () => d.data() }))
      .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
    callback(list);
  });
}

/** Add a reply to an annotation */
export async function addAnnotationReply(
  annotationId: string,
  reply: Omit<PdfAnnotationReply, 'createdAt'>
): Promise<void> {
  const annRef = doc(db, COLLECTION, annotationId);
  const docSnap = await getDoc(annRef);
  if (!docSnap.exists()) return;

  const data = docSnap.data();
  const replies = (data.replies as Array<{ userId: string; userName: string; content: string; mentions?: unknown[]; createdAt: unknown }>) || [];
  const newReply = {
    userId: reply.userId,
    userName: reply.userName,
    content: reply.content,
    mentions: reply.mentions ?? [],
    createdAt: Timestamp.fromDate(new Date()),
  };

  await updateDoc(annRef, {
    replies: [...replies, newReply],
    // Only the replier has seen the new activity; everyone else should see unread again
    readBy: [reply.userId],
  });
}

/** Toggle emoji reaction on the annotation root or on a reply by index. Does not add Slack/email notifications. */
export async function togglePdfAnnotationReaction(
  annotationId: string,
  userId: string,
  emoji: string,
  replyIndex: number | null
): Promise<void> {
  const trimmed = emoji.trim();
  if (!annotationId || !userId || !trimmed) return;

  let added = false;
  let authorId = '';
  let body = '';
  let data: Record<string, unknown> = {};

  await runTransaction(db, async (transaction) => {
    const ref = doc(db, COLLECTION, annotationId);
    const snap = await transaction.get(ref);
    if (!snap.exists()) return;
    data = snap.data() as Record<string, unknown>;

    if (replyIndex === null) {
      const before = normalizeCommentReactions(data.reactions);
      const after = applyReactionToggle(before, emoji, userId);
      added = !(before[trimmed] || []).includes(userId) && (after[trimmed] || []).includes(userId);
      authorId = commentAuthorIdFromData(data);
      body = String(data.content || '');
      transaction.update(ref, { reactions: after });
      return;
    }

    const rawReplies = (data.replies as Array<Record<string, unknown>>) || [];
    if (replyIndex < 0 || replyIndex >= rawReplies.length) return;
    const replies = rawReplies.map((r) => ({ ...r }));
    const r = replies[replyIndex];
    const before = normalizeCommentReactions(r.reactions);
    const after = applyReactionToggle(before, emoji, userId);
    added = !(before[trimmed] || []).includes(userId) && (after[trimmed] || []).includes(userId);
    authorId = commentAuthorIdFromData(r);
    body = String(r.content || '');
    replies[replyIndex] = { ...r, reactions: after };
    transaction.update(ref, { replies });
  });

  if (
    shouldRecordCommentReactionEvent({
      added,
      actorId: userId,
      authorId,
    })
  ) {
    try {
      await recordTaskCommentReactionEvent(db, {
        actorId: userId,
        emoji: trimmed,
        commentId: annotationId,
        authorId,
        comment: {
          content: body,
          projectId: typeof data.projectId === 'string' ? data.projectId : null,
          dealId: typeof data.dealId === 'string' ? data.dealId : null,
          audience: typeof data.audience === 'string' ? data.audience : null,
          documentId: typeof data.documentId === 'string' ? data.documentId : null,
          pageNumber: typeof data.pageNumber === 'number' ? data.pageNumber : null,
        },
      });
    } catch (eventError) {
      console.error('Error recording PDF reaction event:', eventError);
    }
  }
}
